"use client";

import { useState } from "react";
import { Send, AlertCircle, Loader2 } from "lucide-react";
import Link from "next/link";
import { clienteApi } from "@/services/clienteApi";

export default function FormularioContactoPro() {
    const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
    const [nombre, setNombre] = useState('');
    const [email, setEmail] = useState('');
    const [motivo, setMotivo] = useState('');
    const [perfil, setPerfil] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [aceptaPrivacidad, setAceptaPrivacidad] = useState(false);
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validación básica
        const errors: { [key: string]: string } = {};
        if (!nombre.trim()) errors.nombre = "El nombre es obligatorio";
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Introduce un email válido";
        if (!motivo) errors.motivo = "Selecciona un motivo";
        if (!perfil) errors.perfil = "Selecciona un perfil";
        if (!mensaje.trim()) errors.mensaje = "El mensaje no puede estar vacío";
        if (!aceptaPrivacidad) errors.privacidad = "Debes aceptar la política de privacidad";

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setFormErrors({});
        setStatus('loading');

        try {
            const response = await clienteApi.crudo('/api/contacto-pro', {
                method: 'POST',
                cuerpo: { nombre, email, motivo, perfil, mensaje, aceptaPrivacidad },
            });

            if (response.ok) {
                // Full document navigation (not useRouter().push): gtag('config', ...) in
                // (marketing)/layout.tsx only fires on document load, so a client-side push
                // would depend on GA4 enhanced-measurement history tracking being enabled to
                // register a page_view. A document load registers it unconditionally, which is
                // what a destination-URL conversion needs.
                window.location.assign('/gracias');
            } else {
                setStatus('error');
            }
        } catch (error) {
            setStatus('error');
        }
    };

    return (
        <div className="bg-surface-2 border-4 border-black p-8 rounded-[32px] shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] relative overflow-hidden">
            {/* Neo-brutalist decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent-blue/10 rounded-bl-[100px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent-yellow/20 rounded-tr-[80px] pointer-events-none" />

            <div className="relative z-10">
                <h2 className="text-3xl font-display font-black text-ink mb-2">Envíanos un mensaje</h2>
                <p className="text-ink/70 mb-8 font-medium">Completa el formulario y nuestro equipo se pondrá en contacto contigo en un máximo de 48 horas laborables.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="nombre" className="block text-sm font-bold text-ink">
                                Nombre completo <span className="text-danger">*</span>
                            </label>
                            <input
                                id="nombre"
                                type="text"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                className={`w-full px-4 py-3 bg-tint-neutral border-2 ${formErrors.nombre ? 'border-danger bg-tint-danger' : 'border-black'} rounded-xl focus:outline-none focus:ring-4 focus:ring-accent-blue/20 transition-all font-medium`}
                                placeholder="ej. Dra. García"
                            />
                            {formErrors.nombre && <p className="text-danger text-xs font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formErrors.nombre}</p>}
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="email" className="block text-sm font-bold text-ink">
                                Correo electrónico <span className="text-danger">*</span>
                            </label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={`w-full px-4 py-3 bg-tint-neutral border-2 ${formErrors.email ? 'border-danger bg-tint-danger' : 'border-black'} rounded-xl focus:outline-none focus:ring-4 focus:ring-accent-blue/20 transition-all font-medium`}
                                placeholder="ej. contacto@clinica.com"
                            />
                            {formErrors.email && <p className="text-danger text-xs font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formErrors.email}</p>}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="perfil" className="block text-sm font-bold text-ink">
                                ¿A quién representas? <span className="text-danger">*</span>
                            </label>
                            <select
                                id="perfil"
                                value={perfil}
                                onChange={(e) => setPerfil(e.target.value)}
                                className={`w-full px-4 py-3 bg-tint-neutral border-2 ${formErrors.perfil ? 'border-danger bg-tint-danger' : 'border-black'} rounded-xl focus:outline-none focus:ring-4 focus:ring-accent-blue/20 transition-all font-medium appearance-none`}
                            >
                                <option value="" disabled>Selecciona una opción</option>
                                <option value="Psicólogo Independiente">Psicólogo Independiente</option>
                                <option value="Clínica Pequeña (2-5)">Clínica Pequeña (2-5 profesionales)</option>
                                <option value="Centro Grande (+5)">Centro Grande (+5 profesionales)</option>
                                <option value="Paciente / Otro">Paciente / Otro</option>
                            </select>
                            {formErrors.perfil && <p className="text-danger text-xs font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formErrors.perfil}</p>}
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="motivo" className="block text-sm font-bold text-ink">
                                Motivo de contacto <span className="text-danger">*</span>
                            </label>
                            <select
                                id="motivo"
                                value={motivo}
                                onChange={(e) => setMotivo(e.target.value)}
                                className={`w-full px-4 py-3 bg-tint-neutral border-2 ${formErrors.motivo ? 'border-danger bg-tint-danger' : 'border-black'} rounded-xl focus:outline-none focus:ring-4 focus:ring-accent-blue/20 transition-all font-medium appearance-none`}
                            >
                                <option value="" disabled>Selecciona el motivo</option>
                                <option value="Consultas Comerciales / Precios">Consultas Comerciales / Precios</option>
                                <option value="Soporte Técnico">Soporte Técnico</option>
                                <option value="Privacidad y RGPD">Privacidad y Seguridad</option>
                                <option value="Alianzas y Partnerships">Alianzas y Partnerships</option>
                                <option value="Otra consulta">Otra consulta</option>
                            </select>
                            {formErrors.motivo && <p className="text-danger text-xs font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formErrors.motivo}</p>}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="mensaje" className="block text-sm font-bold text-ink">
                            ¿En qué podemos ayudarte? <span className="text-danger">*</span>
                        </label>
                        <textarea
                            id="mensaje"
                            value={mensaje}
                            onChange={(e) => setMensaje(e.target.value)}
                            rows={5}
                            className={`w-full px-4 py-3 bg-tint-neutral border-2 ${formErrors.mensaje ? 'border-danger bg-tint-danger' : 'border-black'} rounded-xl focus:outline-none focus:ring-4 focus:ring-accent-blue/20 transition-all font-medium resize-none`}
                            placeholder="Describe brevemente tu necesidad o duda..."
                        />
                        {formErrors.mensaje && <p className="text-danger text-xs font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formErrors.mensaje}</p>}
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-tint-neutral border-2 border-dashed border-gray-300 rounded-xl">
                        <input
                            type="checkbox"
                            id="privacidad"
                            checked={aceptaPrivacidad}
                            onChange={(e) => setAceptaPrivacidad(e.target.checked)}
                            className="mt-1 w-5 h-5 border-2 border-black rounded accent-ink cursor-pointer flex-shrink-0"
                        />
                        <div className="space-y-1">
                            <label htmlFor="privacidad" className="text-sm text-ink/80 cursor-pointer font-medium leading-tight">
                                He leído y acepto el tratamiento de mis datos personales de acuerdo con la <Link href="/privacidad" className="font-bold text-ink underline decoration-2 underline-offset-2 hover:text-accent-blue transition-colors">Política de Privacidad</Link>. <span className="text-danger">*</span>
                            </label>
                            {formErrors.privacidad && <p className="text-danger text-xs font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formErrors.privacidad}</p>}
                        </div>
                    </div>

                    {status === 'error' && (
                        <div className="p-4 bg-tint-danger border-2 border-danger rounded-xl flex items-center gap-3 text-on-tint-danger font-medium">
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            <p>Hubo un error al enviar el mensaje. Por favor, inténtalo de nuevo más tarde o escríbenos directamente a info@therapyrecap.com.</p>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full bg-ink text-canvas py-4 rounded-xl font-display font-bold text-lg flex items-center justify-center gap-2 hover:bg-ink/90 transition-all disabled:opacity-70 disabled:cursor-not-allowed group border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[4px] active:translate-x-[4px]"
                    >
                        {status === 'loading' ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Enviando...</span>
                            </>
                        ) : (
                            <>
                                <span>Enviar Mensaje</span>
                                <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
